import { z } from 'zod';
import { GitChangeRequestState, GitHostKind, GitLinkTargetKind } from './git.js';
/**
 * Ghi ngược vào git — APM-GIT-k, docs/plan-git-integration.md §8.4, §9.3, §10.3.
 *
 * Ba việc: tạo **nhánh** từ một task / issue, tạo **MR/PR** từ nó, và đăng
 * **một** bình luận link ngược trên mỗi MR đã nối với việc. Hai việc đầu chạy
 * trong request của người bấm (`git:act`), việc thứ ba chạy cuối lượt kéo khi kho
 * bật `postBacklink`. Cả ba chạy **dưới danh nghĩa tài khoản của token** — không
 * phải của người bấm — nên mọi hình trả về ở đây đều mang `accountName` để giao
 * diện nói ra điều đó.
 *
 * Kết quả tạo **không** được ghi tắt vào bản sao: tạo xong thì xin "kéo ngay" và
 * nhánh / MR mới về qua đúng đường kéo, cùng liên kết của nó (tên nhánh và tiêu đề
 * MR mang mã việc).
 */
// ── Tên nhánh ───────────────────────────────────────────────────────────────
export const GIT_BRANCH_NAME_MAX = 200;
/** Vì sao một tên nhánh không dùng được — giao diện có một câu cho mỗi giá trị. */
export const GitBranchNameIssue = z.enum(['empty', 'too_long', 'invalid']);
/**
 * Kiểm tên nhánh theo luật của `git check-ref-format --branch` (phần cả GitLab
 * lẫn GitHub cùng giữ): không khoảng trắng / ký tự điều khiển, không `~ ^ : ? * [
 * \`, không `..`, `@{`, `//`; không bắt đầu bằng `-` hay `/`, không kết thúc bằng
 * `/`, `.` hay `.lock`; không đoạn nào bắt đầu bằng `.`; không phải `@` trần.
 *
 * Kiểm ở đây để người gõ sai nhận câu nói rõ ngay ở ô nhập, thay vì một 400 của
 * máy chủ git mà mỗi loại máy chủ viết một kiểu.
 */
export function validateGitBranchName(raw) {
    const name = raw.trim();
    if (name === '')
        return 'empty';
    if (name.length > GIT_BRANCH_NAME_MAX)
        return 'too_long';
    if (
    // eslint-disable-next-line no-control-regex -- ký tự điều khiển chính là thứ cần chặn
    /[\u0000- \u007f~^:?*[\\]/.test(name) ||
        name.includes('..') ||
        name.includes('@{') ||
        name.includes('//') ||
        name === '@' ||
        name.startsWith('-') ||
        name.startsWith('/') ||
        name.endsWith('/') ||
        name.endsWith('.') ||
        name.split('/').some((part) => part.startsWith('.') || part.endsWith('.lock'))) {
        return 'invalid';
    }
    return null;
}
const BRANCH_NAME_MESSAGE = {
    empty: 'Nhập tên nhánh',
    too_long: `Tên nhánh tối đa ${GIT_BRANCH_NAME_MAX} ký tự`,
    invalid: 'Tên nhánh có ký tự git không cho phép',
};
/** Một tên nhánh hợp lệ — dùng cho cả nhánh mới lẫn nhánh gốc / nguồn / đích. */
export const GitBranchName = z
    .string()
    .trim()
    .superRefine((value, ctx) => {
    const issue = validateGitBranchName(value);
    if (issue)
        ctx.addIssue({ code: 'custom', message: BRANCH_NAME_MESSAGE[issue] });
});
// ── Đường dẫn sâu tới một việc theo mã (§8.4) ───────────────────────────────
/**
 * `<APP_URL>/t/GOLD-123` — đường dẫn tới một việc theo **mã**, không theo id.
 *
 * Bình luận link ngược dùng nó, và nó là thứ điền vào *Custom issue tracker*
 * (GitLab) / *Autolink references* (GitHub): hai tính năng ấy chỉ biết mã viết
 * trong lời commit, không biết uuid nào của ChillPM.
 */
export const GIT_WORK_ITEM_PATH = '/t';
/** Mã việc ở dạng chuẩn: `GOLD-123` (task), `GOLD-I12` (issue vận hành). */
export function gitWorkItemCode(projectKey, kind, number) {
    return `${projectKey.trim().toUpperCase()}-${kind === 'issue' ? 'I' : ''}${number}`;
}
export function gitWorkItemUrl(appUrl, code) {
    return `${appUrl.replace(/\/+$/, '')}${GIT_WORK_ITEM_PATH}/${encodeURIComponent(code)}`;
}
/**
 * `GOLD-123` / `gold-i12` → khoá dự án + loại + số. Không phân biệt hoa thường
 * (tên nhánh viết thường). Không khớp ⇒ `null`.
 */
export function parseWorkItemCode(raw) {
    const match = /^([A-Za-z][A-Za-z0-9_]{0,19})-(I)?([1-9]\d{0,8})$/i.exec(raw.trim());
    if (!match)
        return null;
    return {
        projectKey: match[1].toUpperCase(),
        kind: match[2] ? 'issue' : 'task',
        number: Number(match[3]),
    };
}
/** `GET /api/v1/git/work-items/:code` — mã việc → nơi mở nó. */
export const GitWorkItemRef = z.object({
    kind: GitLinkTargetKind,
    id: z.uuid(),
    projectId: z.uuid(),
    code: z.string(),
});
/**
 * Mẫu điền vào *Settings → Integrations → Custom issue tracker* của GitLab
 * (§8.4): GitLab thay `:id` bằng **phần số** của mã (`GOLD-123` ⇒ `123`), nên
 * khoá dự án nằm sẵn trong mẫu.
 */
export function gitlabIssueTrackerUrl(appUrl, projectKey) {
    return `${appUrl.replace(/\/+$/, '')}${GIT_WORK_ITEM_PATH}/${projectKey.trim().toUpperCase()}-:id`;
}
/**
 * Hai ô của *Settings → Autolink references* của GitHub (§8.4): tiền tố `GOLD-`
 * và mẫu địa chỉ có `<num>`.
 */
export function githubAutolink(appUrl, projectKey) {
    const prefix = `${projectKey.trim().toUpperCase()}-`;
    return {
        prefix,
        urlTemplate: `${appUrl.replace(/\/+$/, '')}${GIT_WORK_ITEM_PATH}/${prefix}<num>`,
    };
}
// ── Văn bản do ChillPM viết vào git ─────────────────────────────────────────
export const GIT_CHANGE_REQUEST_TITLE_MAX = 255;
export const GIT_CHANGE_REQUEST_DESCRIPTION_MAX = 8000;
/** Tiêu đề mặc định của MR tạo từ một việc: `GOLD-123 <tên việc>` (§8.4). */
export function gitChangeRequestTitle(code, title) {
    return `${code} ${title.trim()}`.trim().slice(0, GIT_CHANGE_REQUEST_TITLE_MAX);
}
/**
 * Mô tả mặc định của MR: một dòng dẫn về việc. Viết `Ref` chứ không `Closes` —
 * tiêu đề đã mang mã (ý định `closes`), và mô tả là chỗ người mở MR viết tiếp.
 */
export function gitChangeRequestDescription(code, url) {
    return `Ref ${code}: ${url}`;
}
/**
 * Mở đầu của **mọi** bình luận link ngược. Adapter GitLab dựa vào nó để không
 * tính bình luận của chính ChillPM là "lượt review đầu tiên" của MR: ở GitLab,
 * bình luận là một ghi chú thường của tài khoản token — đúng thứ `firstReviewAt`
 * đếm (§9.2), và đếm nó thì pha "chờ nhận" của mọi MR thành vài phút.
 */
export const GIT_BACKLINK_PREFIX = 'ChillPM: ';
/** Bình luận link ngược (§8.4): `ChillPM: GOLD-123 — <tên việc> <url>`. */
export function gitBacklinkComment(input) {
    return `${GIT_BACKLINK_PREFIX}${input.code} — ${input.title.trim().slice(0, 200)} ${input.url}`;
}
/** Khoá của một cặp (MR, việc) trong `git_change_request.backlink_comments`. */
export function gitBacklinkKey(kind, targetId) {
    return `${kind}:${targetId}`;
}
/** Số bình luận link ngược đăng tối đa trong **một** lượt kéo của một kho. */
export const GIT_BACKLINKS_PER_RUN = 10;
// ── Hộp thoại Tạo nhánh / Tạo MR ────────────────────────────────────────────
/**
 * Vì sao kho này **chưa ghi được** — nút mờ đi kèm đúng câu này (§8.4 "Lưu ý").
 *
 * `read_only` là gợi ý đọc từ `git_connection.can_write` (chốt lúc gắn kho, có
 * thể cũ): nút mờ, nhưng API vẫn phải chịu được 403 thật của máy chủ git.
 */
export const GitWriteBlockedReason = z.enum([
    'host_disabled',
    'connection_unusable',
    'not_supported',
    'read_only',
]);
export const GitWriteRepository = z.object({
    id: z.uuid(),
    name: z.string(),
    fullPath: z.string(),
    webUrl: z.string(),
    hostKind: GitHostKind,
    hostName: z.string(),
    /** Nhánh gốc / nhánh đích mặc định; `null` = kho rỗng. */
    defaultBranch: z.string().nullable(),
    /** Tài khoản đứng tên token — nhánh / MR sẽ được tạo **dưới tên này**. */
    accountName: z.string().nullable(),
    writable: z.boolean(),
    blockedReason: GitWriteBlockedReason.nullable(),
});
/** `GET …/git/write-options?targetKind=&targetId=` (`git:act`). */
export const GitWriteOptionsQuery = z.object({
    targetKind: GitLinkTargetKind,
    targetId: z.uuid(),
});
/**
 * Mọi thứ hai hộp thoại cần trong một lời gọi: kho nào ghi được (và dưới tên ai),
 * tên nhánh / tiêu đề / mô tả mặc định của việc này.
 *
 * ⚠️ Không nhận asOf: thao tác ghi tác động lên "bây giờ"; đang xem quá khứ thì
 * hai nút ẩn đi và lời gọi này không được phát.
 */
export const GitWriteOptions = z.object({
    target: z.object({
        kind: GitLinkTargetKind,
        id: z.uuid(),
        code: z.string(),
        title: z.string(),
        /** Đường dẫn sâu theo mã (`<APP_URL>/t/GOLD-123`). */
        url: z.string(),
    }),
    /** Từ `buildBranchName` với định dạng của dự án — sửa được trong hộp thoại. */
    branchName: z.string(),
    changeRequestTitle: z.string(),
    changeRequestDescription: z.string(),
    repositories: z.array(GitWriteRepository),
});
/** `POST …/git/repositories/:repositoryId/branches` (`git:act`). */
export const CreateGitBranchInput = z.object({
    targetKind: GitLinkTargetKind,
    targetId: z.uuid(),
    /** Vắng = tên mặc định của việc (`buildBranchName`). */
    name: GitBranchName.optional(),
    /** Nhánh gốc; vắng = nhánh mặc định của kho. */
    ref: GitBranchName.optional(),
});
export const GitBranchCreated = z.object({
    repositoryId: z.uuid(),
    name: z.string(),
    /** Nhánh gốc đã dùng. */
    ref: z.string(),
    accountName: z.string().nullable(),
});
/** `POST …/git/repositories/:repositoryId/change-requests` (`git:act`). */
export const CreateGitChangeRequestInput = z.object({
    targetKind: GitLinkTargetKind,
    targetId: z.uuid(),
    sourceBranch: GitBranchName,
    /** Vắng = nhánh mặc định của kho. */
    targetBranch: GitBranchName.optional(),
    /** Vắng = `GOLD-123 <tên việc>`. */
    title: z.string().trim().min(1).max(GIT_CHANGE_REQUEST_TITLE_MAX).optional(),
    /** Vắng = một dòng dẫn về việc. Chuỗi rỗng = không mô tả. */
    description: z.string().trim().max(GIT_CHANGE_REQUEST_DESCRIPTION_MAX).optional(),
    /** Mặc định **nháp** (§8.4). */
    draft: z.boolean().default(true),
});
export const GitChangeRequestCreated = z.object({
    repositoryId: z.uuid(),
    /** `false` = nhánh nguồn đã có MR đang mở — đây là chính MR ấy, không tạo thêm. */
    created: z.boolean(),
    number: z.number().int().positive(),
    title: z.string(),
    webUrl: z.string(),
    state: GitChangeRequestState,
    isDraft: z.boolean(),
    sourceBranch: z.string(),
    targetBranch: z.string(),
    accountName: z.string().nullable(),
});
//# sourceMappingURL=git-write.js.map